import React, { useState, useEffect } from "react";
import {
  FileText,
  Download,
  RefreshCw,
  ClipboardCheck,
  Calendar,
  Mail,
  Loader2,
  Trash2,
} from "lucide-react";
import api from "../api/client";

const typeLabels = {
  weekly: "Weekly Report",
  audit: "Audit Report",
};

const statusColors = {
  generating: "bg-amber-900/30 text-amber-400",
  completed: "bg-green-900/30 text-green-400",
  failed: "bg-red-900/30 text-red-400",
};

export default function Reports({ selectedSite }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    if (!selectedSite) return;
    setLoading(true);
    api
      .get(`/websites/${selectedSite.id}/reports/`)
      .then((res) => {
        setReports(res.data.results || res.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedSite]);

  const generateReport = (type) => {
    if (!selectedSite) return;
    setGenerating(type);
    api
      .post(`/websites/${selectedSite.id}/reports/generate/`, {
        report_type: type,
      })
      .then(() => {
        // Poll for completion
        setTimeout(() => {
          api
            .get(`/websites/${selectedSite.id}/reports/`)
            .then((res) => {
              setReports(res.data.results || res.data);
            })
            .catch(() => {});
        }, 3000);
      })
      .catch(() => {})
      .finally(() => setGenerating(null));
  };

  const downloadReport = (report) => {
    if (!report.download_url) return;
    api
      .get(report.download_url, { responseType: "blob" })
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          report.filename || `report-${report.id}.pdf`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => {});
  };

  const deleteReport = (report) => {
    if (!window.confirm(`Delete this ${report.report_type} report?`)) return;
    api
      .delete(
        selectedSite
          ? `/websites/${selectedSite.id}/reports/${report.id}/`
          : `/reports/${report.id}/`
      )
      .then(() => {
        setReports((prev) => prev.filter((r) => r.id !== report.id));
      })
      .catch(() => {});
  };

  const filteredReports =
    filter === "all"
      ? reports
      : reports.filter((r) => r.report_type === filter);

  if (!selectedSite) {
    return (
      <div className="text-gray-500 text-center py-20">
        Select a website to view reports
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Reports</h2>
        <div className="flex gap-2">
          <button
            onClick={() => generateReport("weekly")}
            disabled={generating}
            className="flex items-center gap-2 px-4 py-2 bg-copper hover:bg-copper-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            {generating === "weekly" ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <FileText size={14} />
            )}
            Generate Weekly Report
          </button>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2">
        {[
          { key: "all", label: "All" },
          { key: "weekly", label: "Weekly" },
          { key: "audit", label: "Audit" },
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              filter === key
                ? "bg-copper/20 text-copper"
                : "bg-dark-300 text-gray-400 hover:text-white"
            }`}
          >
            {label}
            {key !== "all" && (
              <span className="ml-1.5 text-gray-500">
                {reports.filter((r) => r.report_type === key).length}
              </span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="animate-pulse space-y-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-16 bg-dark-300 rounded-xl" />
          ))}
        </div>
      ) : filteredReports.length === 0 ? (
        <div className="bg-dark-300 border border-dark-50 rounded-xl p-12 text-center text-gray-500">
          <FileText size={40} className="mx-auto mb-3 opacity-30" />
          <p>No reports generated yet.</p>
          <p className="text-sm mt-1">
            Click "Generate Weekly Report" to create your first report.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredReports.map((report) => (
            <div
              key={report.id}
              className="bg-dark-300 border border-dark-50 rounded-xl p-4 flex items-center justify-between hover:border-dark-50/80 transition-colors"
            >
              <div className="flex items-center gap-4">
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    report.report_type === "weekly"
                      ? "bg-copper/10 text-copper"
                      : "bg-blue-900/20 text-blue-400"
                  }`}
                >
                  {report.report_type === "weekly" ? (
                    <Calendar size={18} />
                  ) : (
                    <ClipboardCheck size={18} />
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-200">
                      {typeLabels[report.report_type] || report.report_type}
                    </span>
                    <span
                      className={`text-xs px-1.5 py-0.5 rounded ${
                        statusColors[report.status] || ""
                      }`}
                    >
                      {report.status}
                    </span>
                    {report.emailed && (
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <Mail size={10} /> Emailed
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {report.website_domain} —{" "}
                    {new Date(report.created_at).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                      hour: "numeric",
                      minute: "2-digit",
                    })}
                    {report.file_size > 0 && (
                      <span className="ml-2">
                        ({(report.file_size / 1024).toFixed(0)} KB)
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {report.status === "completed" && report.download_url && (
                  <button
                    onClick={() => downloadReport(report)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-dark-50/50 hover:bg-dark-50 text-gray-300 hover:text-white text-xs font-medium rounded-lg transition-colors"
                  >
                    <Download size={13} />
                    Download PDF
                  </button>
                )}
                {report.status === "generating" && (
                  <Loader2
                    size={16}
                    className="text-amber-400 animate-spin"
                  />
                )}
                <button
                  onClick={() => deleteReport(report)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-dark-50/50 hover:bg-red-900/30 text-gray-500 hover:text-red-400 text-xs font-medium rounded-lg transition-colors"
                  title="Delete report"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
